import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Mail,
  Copy,
  Check,
  User,
  Building,
  Loader2,
  AlertCircle,
  Users,
  CheckSquare,
  Square,
} from "lucide-react";
import Footer from "@/components/Footer";
import { trackPageView, trackRepSelect } from "@/lib/analytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function TakeAction() {
  const [constituencies, setConstituencies] = useState([]);
  const [representatives, setRepresentatives] = useState([]);
  const [selectedConstituency, setSelectedConstituency] = useState("");
  const [selectedReps, setSelectedReps] = useState([]);
  const [emailTemplate, setEmailTemplate] = useState({ subject: "", body: "" });
  const [editedSubject, setEditedSubject] = useState("");
  const [editedBody, setEditedBody] = useState("");
  const [userName, setUserName] = useState("");
  const [userAddress, setUserAddress] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingReps, setLoadingReps] = useState(false);
  const [copied, setCopied] = useState(false);
  const [houseFilter, setHouseFilter] = useState("all");
  const [councils, setCouncils] = useState([]);
  const [selectedCouncil, setSelectedCouncil] = useState("");
  const [councillors, setCouncillors] = useState([]);

  // Fetch constituencies and email template on mount
  useEffect(() => {
    trackPageView("take-action");
    const fetchInitialData = async () => {
      try {
        const [constRes, templateRes, councilsRes] = await Promise.all([
          axios.get(`${API}/constituencies`),
          axios.get(`${API}/email-template`),
          axios.get(`${API}/councils`),
        ]);
        setConstituencies(constRes.data);
        setCouncils(councilsRes.data);
        setEmailTemplate(templateRes.data);
        setEditedSubject(templateRes.data.subject);
        setEditedBody(templateRes.data.body);
      } catch (error) {
        console.error("Error fetching initial data:", error);
        toast.error("Failed to load data. Please refresh the page.");
      } finally {
        setLoading(false);
      }
    };
    fetchInitialData();
  }, []);

  // Fetch representatives when constituency changes
  useEffect(() => {
    const fetchRepresentatives = async () => {
      if (!selectedConstituency) {
        setRepresentatives([]);
        setSelectedReps([]);
        return;
      }
      setLoadingReps(true);
      setSelectedReps([]);
      try {
        const [tdsRes, senatorsRes] = await Promise.all([
          axios.get(`${API}/tds`, { params: { constituency: selectedConstituency } }),
          axios.get(`${API}/senators`),
        ]);
        const allReps = [...tdsRes.data, ...senatorsRes.data];
        setRepresentatives(allReps);
      } catch (error) {
        console.error("Error fetching representatives:", error);
        toast.error("Failed to load representatives.");
      } finally {
        setLoadingReps(false);
      }
    };
    fetchRepresentatives();
  }, [selectedConstituency]);

  // Fetch councillors when council changes
  useEffect(() => {
    if (!selectedCouncil) {
      setCouncillors([]);
      return;
    }
    axios
      .get(`${API}/councillors`, { params: { council: selectedCouncil } })
      .then((res) => setCouncillors(res.data))
      .catch(() => toast.error("Failed to load councillors."));
  }, [selectedCouncil]);

  // Filter representatives by house
  const filteredReps = representatives.filter((rep) => {
    if (houseFilter === "all") return true;
    return rep.house === houseFilter;
  });

  // Toggle representative selection
  const toggleRepSelection = (rep) => {
    const repKey = `${rep.member_id}-${rep.house}`;
    const isSelected = selectedReps.some(
      (r) => `${r.member_id}-${r.house}` === repKey
    );

    if (isSelected) {
      setSelectedReps(selectedReps.filter(
        (r) => `${r.member_id}-${r.house}` !== repKey
      ));
    } else {
      setSelectedReps([...selectedReps, rep]);
    }
  };

  // Check if a rep is selected
  const isRepSelected = (rep) => {
    return selectedReps.some(
      (r) => `${r.member_id}-${r.house}` === `${rep.member_id}-${rep.house}`
    );
  };

  // Select all filtered reps
  const selectAllFiltered = () => {
    const currentFilteredKeys = new Set(
      filteredReps.map((r) => `${r.member_id}-${r.house}`)
    );
    const otherSelected = selectedReps.filter(
      (r) => !currentFilteredKeys.has(`${r.member_id}-${r.house}`)
    );
    setSelectedReps([...otherSelected, ...filteredReps]);
  };

  // Deselect all filtered reps
  const deselectAllFiltered = () => {
    const currentFilteredKeys = new Set(
      filteredReps.map((r) => `${r.member_id}-${r.house}`)
    );
    setSelectedReps(
      selectedReps.filter(
        (r) => !currentFilteredKeys.has(`${r.member_id}-${r.house}`)
      )
    );
  };

  // Check if all filtered are selected
  const allFilteredSelected = filteredReps.length > 0 && 
    filteredReps.every((rep) => isRepSelected(rep));

  // Generate email address for a rep - sanitize names
  const getRepEmail = (rep) => {
    if (rep.email) return rep.email;
    
    // Sanitize names: remove spaces, accents, and special characters
    const sanitizeName = (name) => {
      return name
        .toLowerCase()
        .normalize('NFD')  // Decompose accented characters
        .replace(/[\u0300-\u036f]/g, '')  // Remove accent marks
        .replace(/[''"]/g, '')  // Remove apostrophes and quotes
        .replace(/\s+/g, '')  // Remove all spaces
        .replace(/[^a-z]/g, '');  // Keep only letters
    };
    
    const firstName = sanitizeName(rep.first_name);
    const lastName = sanitizeName(rep.last_name);
    
    return `${firstName}.${lastName}@oireachtas.ie`;
  };

  // Generate personalized email body
  const getPersonalizedBody = () => {
    let body = editedBody;
    if (selectedReps.length === 1) {
      body = body.replace("[REPRESENTATIVE_NAME]", selectedReps[0].full_name);
    } else if (selectedReps.length > 1) {
      body = body.replace("[REPRESENTATIVE_NAME]", "Members of the Oireachtas");
    }
    if (userName) {
      body = body.replace("[YOUR_NAME]", userName);
    }
    if (userAddress) {
      body = body.replace("[YOUR_ADDRESS]", userAddress);
    }
    return body;
  };

  // Generate mailto link for multiple recipients
  const generateMailtoLink = () => {
    if (selectedReps.length === 0) return "#";
    
    const emailAddresses = selectedReps.map(getRepEmail).join(",");
    const subject = encodeURIComponent(editedSubject);
    const body = encodeURIComponent(getPersonalizedBody());
    
    return `mailto:${emailAddresses}?subject=${subject}&body=${body}`;
  };

  // Open email client - handles large recipient lists
  const openEmailClient = (useGmail = false) => {
    if (selectedReps.length === 0) {
      toast.warning("Please select at least one representative");
      return;
    }

    // Track rep selection
    trackRepSelect(selectedReps.length, selectedConstituency);

    const emailAddresses = selectedReps.map(getRepEmail);
    const subject = encodeURIComponent(editedSubject);
    const body = encodeURIComponent(getPersonalizedBody());
    
    if (useGmail) {
      // Open Gmail compose directly
      const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${emailAddresses.join(",")}&su=${subject}&body=${body}`;
      
      if (gmailUrl.length > 8000 || selectedReps.length > 20) {
        // Gmail URL too long - copy emails first
        const emails = emailAddresses.join(", ");
        navigator.clipboard.writeText(emails).then(() => {
          toast.success(
            `${selectedReps.length} email addresses copied! Paste them in Gmail's "To" field.`,
            { duration: 5000 }
          );
          const shortGmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&su=${subject}&body=${body}`;
          window.open(shortGmailUrl, '_blank');
        }).catch(() => {
          window.open(gmailUrl, '_blank');
        });
      } else {
        window.open(gmailUrl, '_blank');
      }
    } else {
      // Use mailto protocol
      const mailtoUrl = `mailto:${emailAddresses.join(",")}?subject=${subject}&body=${body}`;
      
      if (mailtoUrl.length > 1800 || selectedReps.length > 10) {
        const emails = emailAddresses.join("; ");
        navigator.clipboard.writeText(emails).then(() => {
          toast.success(
            `${selectedReps.length} email addresses copied to clipboard! Paste them in your email client.`,
            { duration: 5000 }
          );
          const shortUrl = `mailto:?subject=${subject}&body=${body}`;
          window.location.href = shortUrl;
        }).catch(() => {
          window.location.href = mailtoUrl;
        });
      } else {
        window.location.href = mailtoUrl;
      }
    }
  };

  // Copy email addresses to clipboard
  const copyEmailAddresses = async () => {
    if (selectedReps.length === 0) {
      toast.warning("Please select at least one representative");
      return;
    }
    try {
      const emails = selectedReps.map(getRepEmail).join("; ");
      await navigator.clipboard.writeText(emails);
      toast.success(`${selectedReps.length} email address${selectedReps.length > 1 ? 'es' : ''} copied!`);
    } catch (error) {
      toast.error("Failed to copy email addresses");
    }
  };

  // Copy full email to clipboard
  const copyToClipboard = async () => {
    try {
      const emails = selectedReps.map(getRepEmail).join("; ");
      const fullEmail = `To: ${emails}\nSubject: ${editedSubject}\n\n${getPersonalizedBody()}`;
      await navigator.clipboard.writeText(fullEmail);
      setCopied(true);
      toast.success("Email copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error("Failed to copy to clipboard");
    }
  };

  // Reset email template
  const resetTemplate = () => {
    setEditedSubject(emailTemplate.subject);
    setEditedBody(emailTemplate.body);
    toast.info("Email template reset to default");
  };

  // Get party color class
  const getPartyColor = (party) => {
    const partyLower = party.toLowerCase();
    if (partyLower.includes("fine gael")) return "bg-blue-600";
    if (partyLower.includes("fianna")) return "bg-green-700";
    if (partyLower.includes("sinn")) return "bg-teal-600";
    if (partyLower.includes("labour")) return "bg-red-600";
    if (partyLower.includes("green")) return "bg-emerald-600";
    if (partyLower.includes("social democrat")) return "bg-purple-600";
    if (partyLower.includes("people before profit") || partyLower.includes("pbp")) return "bg-rose-700";
    if (partyLower.includes("solidarity")) return "bg-red-700";
    if (partyLower.includes("aontú")) return "bg-amber-700";
    return "bg-slate-600";
  };

  if (loading) {
    return (
      <main className="pt-24 min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-emerald-500 animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Loading...</p>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="take-action-page" className="pt-24 bg-slate-950 min-h-screen">
      {/* Header */}
      <section className="py-12 md:py-16" data-testid="take-action-header">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <span className="text-sm font-semibold tracking-widest uppercase text-orange-500">
              Make Your Voice Heard
            </span>
            <h1 className="font-serif text-4xl md:text-5xl font-bold text-slate-50 mt-4 mb-6">
              Email Your <span className="text-emerald-400">Representatives</span>
            </h1>
            <p className="text-lg text-slate-300">
              Select your constituency to find your TDs and Senators, then send them
              a ready-made email asking them to protect Ireland's neutrality.
              <span className="text-emerald-400 font-medium"> You can select multiple representatives!</span>
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-5 gap-8">
            {/* Left Column - Representative Selection */}
            <div className="lg:col-span-2 space-y-6">
              {/* Constituency Selector */}
              <div
                className="bg-slate-900/50 border border-slate-800 rounded-xl p-6"
                data-testid="constituency-selector"
              >
                <label className="block text-sm font-medium text-slate-300 mb-3">
                  1. Select Your Constituency
                </label>
                <Select
                  value={selectedConstituency}
                  onValueChange={setSelectedConstituency}
                >
                  <SelectTrigger
                    className="w-full bg-slate-950 border-slate-700 text-slate-100 h-12"
                    data-testid="constituency-dropdown"
                  >
                    <SelectValue placeholder="Choose your constituency..." />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700">
                    {constituencies.map((const_item) => (
                      <SelectItem
                        key={const_item.name}
                        value={const_item.name}
                        className="text-slate-100 focus:bg-slate-800"
                        data-testid={`constituency-option-${const_item.name}`}
                      >
                        {const_item.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Council Selector */}
              <div
                className="bg-slate-900/50 border border-slate-800 rounded-xl p-6"
                data-testid="council-selector"
              >
                <label className="block text-sm font-medium text-slate-300 mb-3">
                  County Council (Optional)
                </label>
                <Select
                  value={selectedCouncil}
                  onValueChange={setSelectedCouncil}
                >
                  <SelectTrigger
                    className="w-full bg-slate-950 border-slate-700 text-slate-100 h-12"
                    data-testid="council-dropdown"
                  >
                    <SelectValue placeholder="Choose your council..." />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700 max-h-60">
                    {councils.map((council) => (
                      <SelectItem
                        key={council}
                        value={council}
                        className="text-slate-100 focus:bg-slate-800"
                        data-testid={`council-option-${council}`}
                      >
                        {council}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {councillors.length > 0 && (
                  <p className="text-xs text-slate-400 mt-2">
                    {councillors.length} councillors in {selectedCouncil}
                  </p>
                )}
              </div>

              {/* House Filter */}
              {selectedConstituency && (
                <div className="flex gap-2 flex-wrap" data-testid="house-filter">
                  <Button
                    variant={houseFilter === "all" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setHouseFilter("all")}
                    className={
                      houseFilter === "all"
                        ? "bg-emerald-600 hover:bg-emerald-500"
                        : "border-slate-700 text-slate-300"
                    }
                  >
                    All
                  </Button>
                  <Button
                    variant={houseFilter === "dail" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setHouseFilter("dail")}
                    className={
                      houseFilter === "dail"
                        ? "bg-emerald-600 hover:bg-emerald-500"
                        : "border-slate-700 text-slate-300"
                    }
                  >
                    TDs (Dáil)
                  </Button>
                  <Button
                    variant={houseFilter === "seanad" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setHouseFilter("seanad")}
                    className={
                      houseFilter === "seanad"
                        ? "bg-emerald-600 hover:bg-emerald-500"
                        : "border-slate-700 text-slate-300"
                    }
                  >
                    Senators
                  </Button>
                </div>
              )}

              {/* Representatives List */}
              <div
                className="bg-slate-900/50 border border-slate-800 rounded-xl p-6"
                data-testid="representatives-list"
              >
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium text-slate-300">
                    2. Select Representatives
                  </label>
                  {filteredReps.length > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={allFilteredSelected ? deselectAllFiltered : selectAllFiltered}
                      className="text-xs text-emerald-400 hover:text-emerald-300 hover:bg-emerald-900/20"
                      data-testid="select-all-btn"
                    >
                      {allFilteredSelected ? (
                        <>
                          <Square className="w-4 h-4 mr-1" />
                          Deselect All
                        </>
                      ) : (
                        <>
                          <CheckSquare className="w-4 h-4 mr-1" />
                          Select All
                        </>
                      )}
                    </Button>
                  )}
                </div>

                {/* Selection Count Badge */}
                {selectedReps.length > 0 && (
                  <div className="mb-3 flex items-center gap-2">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-600/20 border border-emerald-500/30 text-emerald-400 text-sm font-medium">
                      <Users className="w-4 h-4" />
                      {selectedReps.length} selected
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedReps([])}
                      className="text-xs text-slate-400 hover:text-slate-200"
                    >
                      Clear all
                    </Button>
                  </div>
                )}

                {loadingReps ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 text-emerald-500 animate-spin" />
                  </div>
                ) : !selectedConstituency ? (
                  <div className="text-center py-8 text-slate-500">
                    <Building className="w-10 h-10 mx-auto mb-3 opacity-50" />
                    <p>Select a constituency to see representatives</p>
                  </div>
                ) : filteredReps.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <AlertCircle className="w-10 h-10 mx-auto mb-3 opacity-50" />
                    <p>No representatives found</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2">
                    {filteredReps.map((rep) => {
                      const selected = isRepSelected(rep);
                      return (
                        <button
                          key={`${rep.member_id}-${rep.house}`}
                          onClick={() => toggleRepSelection(rep)}
                          data-testid={`rep-card-${rep.member_id}`}
                          className={`w-full text-left p-3 rounded-lg border transition-all ${
                            selected
                              ? "border-emerald-500 bg-emerald-900/20"
                              : "border-slate-700 bg-slate-800/30 hover:border-slate-600"
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <Checkbox
                              checked={selected}
                              className="border-slate-600 data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600"
                            />
                            <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center flex-shrink-0">
                              <User className="w-4 h-4 text-slate-400" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-slate-100 text-sm truncate">
                                {rep.full_name}
                              </p>
                              <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                <span
                                  className={`px-1.5 py-0.5 rounded text-xs font-medium text-white ${getPartyColor(
                                    rep.party
                                  )}`}
                                >
                                  {rep.party}
                                </span>
                                <span className="text-xs text-slate-500">
                                  {rep.house === "dail" ? "TD" : "Senator"}
                                </span>
                              </div>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Councillors List */}
              {councillors.length > 0 && (
                <div
                  className="bg-slate-900/50 border border-slate-800 rounded-xl p-6"
                  data-testid="councillors-list"
                >
                  <label className="block text-sm font-medium text-slate-300 mb-3">
                    Your County Councillors ({councillors.length})
                  </label>
                  <div className="space-y-2 max-h-[300px] overflow-y-auto pr-2">
                    {councillors.map((c, idx) => (
                      <div
                        key={`${c.name}-${idx}`}
                        className="p-3 rounded-lg border border-slate-700 bg-slate-800/30"
                        data-testid={`councillor-card-${idx}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center">
                            <User className="w-4 h-4 text-slate-300" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-slate-100">
                              {c.name}
                            </p>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className="text-xs px-2 py-0.5 rounded-full bg-slate-700 text-slate-300">
                                {c.party}
                              </span>
                              <span className="text-xs text-slate-500">
                                {c.lea}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-slate-500 mt-3">
                    Source: 2024 Local Election Results — Dept. of Housing, Local Government and Heritage
                  </p>
                </div>
              )}

              {/* User Details */}
              <div
                className="bg-slate-900/50 border border-slate-800 rounded-xl p-6"
                data-testid="user-details"
              >
                <label className="block text-sm font-medium text-slate-300 mb-3">
                  3. Your Details (Optional)
                </label>
                <div className="space-y-4">
                  <div>
                    <label className="text-xs text-slate-500 mb-1 block">
                      Your Name
                    </label>
                    <Input
                      value={userName}
                      onChange={(e) => setUserName(e.target.value)}
                      placeholder="Enter your name"
                      className="bg-slate-950 border-slate-700 text-slate-100"
                      data-testid="user-name-input"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 mb-1 block">
                      Your Address
                    </label>
                    <Input
                      value={userAddress}
                      onChange={(e) => setUserAddress(e.target.value)}
                      placeholder="Enter your address"
                      className="bg-slate-950 border-slate-700 text-slate-100"
                      data-testid="user-address-input"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Email Template */}
            <div className="lg:col-span-3">
              <div
                className="email-document p-6 md:p-8 sticky top-24"
                data-testid="email-template-editor"
              >
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-serif text-xl font-medium text-slate-100">
                    Your Email
                  </h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={resetTemplate}
                    className="text-slate-400 hover:text-slate-200"
                    data-testid="reset-template-btn"
                  >
                    Reset to Default
                  </Button>
                </div>

                {/* Selected Reps Display */}
                {selectedReps.length > 0 && (
                  <div
                    className="mb-6 p-4 bg-slate-800/50 rounded-lg border border-slate-700"
                    data-testid="selected-reps-display"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs text-slate-500">
                        Sending to {selectedReps.length} recipient{selectedReps.length > 1 ? 's' : ''}:
                      </p>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={copyEmailAddresses}
                        className="text-xs text-emerald-400 hover:text-emerald-300 h-6 px-2"
                      >
                        <Copy className="w-3 h-3 mr-1" />
                        Copy emails
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {selectedReps.slice(0, 5).map((rep) => (
                        <span
                          key={`${rep.member_id}-${rep.house}`}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-slate-700/50 text-sm"
                        >
                          <span className="text-emerald-400 font-medium">{rep.full_name}</span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleRepSelection(rep);
                            }}
                            className="text-slate-500 hover:text-slate-300 ml-1"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                      {selectedReps.length > 5 && (
                        <span className="inline-flex items-center px-2 py-1 rounded-md bg-slate-700/50 text-sm text-slate-400">
                          +{selectedReps.length - 5} more
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Subject Line */}
                <div className="mb-4">
                  <label className="text-xs text-slate-500 mb-2 block">
                    Subject
                  </label>
                  <Input
                    value={editedSubject}
                    onChange={(e) => setEditedSubject(e.target.value)}
                    className="bg-slate-900 border-slate-700 text-slate-100 font-medium"
                    data-testid="email-subject-input"
                  />
                </div>

                {/* Email Body */}
                <div className="mb-6">
                  <label className="text-xs text-slate-500 mb-2 block">
                    Message
                  </label>
                  <Textarea
                    value={editedBody}
                    onChange={(e) => setEditedBody(e.target.value)}
                    className="bg-slate-900 border-slate-700 text-slate-200 min-h-[400px] leading-relaxed"
                    data-testid="email-body-textarea"
                  />
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col gap-3">
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button
                      className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white py-6 rounded-full font-bold text-lg transition-all hover:-translate-y-0.5 shadow-[0_0_20px_-5px_rgba(22,163,74,0.4)]"
                      disabled={selectedReps.length === 0}
                      data-testid="send-gmail-btn"
                      onClick={() => openEmailClient(true)}
                    >
                      <Mail className="w-5 h-5 mr-2" />
                      {selectedReps.length > 1 
                        ? `Open Gmail (${selectedReps.length} Recipients)`
                        : "Open in Gmail"
                      }
                    </Button>
                    <Button
                      variant="outline"
                      onClick={copyToClipboard}
                      className="border-slate-700 text-slate-300 hover:text-white py-6 px-6 rounded-full"
                      data-testid="copy-email-btn"
                      disabled={selectedReps.length === 0}
                    >
                      {copied ? (
                        <Check className="w-5 h-5 text-emerald-400" />
                      ) : (
                        <Copy className="w-5 h-5" />
                      )}
                    </Button>
                  </div>
                  <Button
                    variant="outline"
                    className="w-full border-slate-700 text-slate-400 hover:text-white py-4 rounded-full"
                    disabled={selectedReps.length === 0}
                    data-testid="send-email-btn"
                    onClick={() => openEmailClient(false)}
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Use Default Email App (Outlook, Apple Mail, etc.)
                  </Button>
                </div>

                {selectedReps.length === 0 && (
                  <p className="text-center text-sm text-slate-500 mt-4">
                    Select one or more representatives above to enable sending
                  </p>
                )}

                {selectedReps.length > 10 && (
                  <p className="text-center text-sm text-orange-400 mt-4">
                    Tip: With {selectedReps.length} recipients, we'll copy email addresses to your clipboard. 
                    Paste them in the "To" field of your email client.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
